/** PREREQ Stage 3 — Header & Navigation */
(() => {
  class PrereqHeader {
    constructor(root) {
      this.root = root;
      this.menuOpenButton = root.querySelector('[data-prereq-menu-open]');
      this.menu = root.querySelector('[data-prereq-mobile-menu]');
      this.menuCloseButtons = root.querySelectorAll('[data-prereq-menu-close]');
      this.mobileDetails = root.querySelectorAll('[data-prereq-mobile-details]');

      this.searchOpenButtons = root.querySelectorAll('[data-prereq-search-open]');
      this.search = root.querySelector('[data-prereq-search-overlay]');
      this.searchCloseButtons = root.querySelectorAll('[data-prereq-search-close]');
      this.searchInput = root.querySelector('[data-prereq-search-input]');
      this.searchResults = root.querySelector('[data-prereq-search-results]');
      this.searchResultsGrid = root.querySelector('[data-prereq-search-results-grid]');
      this.searchStatus = root.querySelector('[data-prereq-search-status]');
      this.searchHint = root.querySelector('[data-prereq-search-hint]');
      this.searchViewAll = root.querySelector('[data-prereq-search-view-all]');
      this.predictiveSearchUrl = this.search?.dataset.prereqPredictiveSearchUrl || '/search/suggest';
      this.fullSearchUrl = this.search?.dataset.prereqSearchUrl || '/search';
      this.searchFallbackImage = this.search?.dataset.prereqSearchFallbackImage || '';
      this.currency = this.search?.dataset.prereqCurrency || 'AUD';
      this.searchTimer = null;
      this.searchAbortController = null;

      this.cartOpenButton = root.querySelector('[data-prereq-cart-open]');
      this.cart = root.querySelector('[data-prereq-cart-drawer]');
      this.cartCloseButtons = root.querySelectorAll('[data-prereq-cart-close]');
      this.cartMessage = root.querySelector('[data-prereq-cart-message]');
      this.cartChangeUrl = this.cart?.dataset.cartChangeUrl || '/cart/change';
      this.cartUpdating = false;

      this.desktopDropdowns = root.querySelectorAll('[data-prereq-desktop-dropdown]');
      this.desktopMedia = window.matchMedia('(min-width: 990px)');
      this.activeOverlay = null;
      this.lastFocusedElement = null;
      this.isSticky = root.classList.contains('prereq-header-shell--sticky');
      this.destroyed = false;
      this.boundHandleScroll = () => this.handleScroll();

      this.menuOpenButton?.addEventListener('click', () => this.openOverlay('menu'));
      this.menuCloseButtons.forEach((button) => button.addEventListener('click', () => this.closeOverlay('menu')));

      this.searchOpenButtons.forEach((button) => button.addEventListener('click', () => this.openOverlay('search', button)));
      this.searchCloseButtons.forEach((button) => button.addEventListener('click', () => this.closeOverlay('search')));
      this.searchInput?.addEventListener('input', () => this.handleSearchInput());

      this.cartOpenButton?.addEventListener('click', () => this.openOverlay('cart'));
      this.cartCloseButtons.forEach((button) => button.addEventListener('click', () => this.closeOverlay('cart')));
      this.cart?.addEventListener('click', (event) => this.handleCartClick(event));

      this.desktopDropdowns.forEach((dropdown) => {
        dropdown.addEventListener('toggle', () => this.handleDesktopDropdownToggle(dropdown));
      });

      this.mobileDetails.forEach((details) => {
        details.addEventListener('toggle', () => this.handleMobileDetailsToggle(details));
      });

      document.addEventListener('keydown', (event) => this.handleKeydown(event));
      document.addEventListener('click', (event) => this.handleDocumentClick(event));
      this.desktopMedia.addEventListener?.('change', (event) => this.handleBreakpointChange(event));

      if (this.isSticky) {
        this.handleScroll();
        window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
      }
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.activeOverlay = null;
      window.clearTimeout(this.searchTimer);
      this.searchAbortController?.abort();
      if (this.isSticky) window.removeEventListener('scroll', this.boundHandleScroll);
      document.body.classList.remove('prereq-overlay-open');
    }

    handleScroll() {
      if (this.destroyed) return;
      this.root.classList.toggle('is-scrolled', window.scrollY > 18);
    }

    getOverlay(type) {
      if (type === 'menu') return this.menu;
      if (type === 'search') return this.search;
      if (type === 'cart') return this.cart;
      return null;
    }

    getToggle(type) {
      if (type === 'menu') return this.menuOpenButton;
      if (type === 'search') return this.searchOpenButtons[0] || null;
      if (type === 'cart') return this.cartOpenButton;
      return null;
    }

    setToggleExpanded(type, expanded) {
      const value = expanded ? 'true' : 'false';
      if (type === 'search') {
        this.searchOpenButtons.forEach((button) => button.setAttribute('aria-expanded', value));
        return;
      }
      this.getToggle(type)?.setAttribute('aria-expanded', value);
    }

    closeDesktopDropdowns(except = null) {
      this.desktopDropdowns.forEach((dropdown) => {
        if (dropdown !== except && dropdown.open) dropdown.removeAttribute('open');
      });
    }

    openOverlay(type, trigger = null) {
      if (this.destroyed) return;
      const overlay = this.getOverlay(type);
      if (!overlay) return;

      this.closeDesktopDropdowns();

      if (this.activeOverlay && this.activeOverlay !== type) {
        this.closeOverlay(this.activeOverlay, false);
      }

      this.lastFocusedElement = trigger || document.activeElement;
      this.activeOverlay = type;
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      this.setToggleExpanded(type, true);
      document.body.classList.add('prereq-overlay-open');

      requestAnimationFrame(() => {
        if (type === 'search' && this.searchInput) {
          this.searchInput.focus();
          return;
        }
        const firstFocusable = overlay.querySelector('button:not([tabindex="-1"]), input, a[href], summary');
        firstFocusable?.focus();
      });
    }

    closeOverlay(type, restoreFocus = true) {
      if (this.destroyed) return;
      const overlay = this.getOverlay(type);
      if (!overlay) return;

      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      this.setToggleExpanded(type, false);

      if (this.activeOverlay === type) this.activeOverlay = null;
      if (!this.activeOverlay) document.body.classList.remove('prereq-overlay-open');

      if (restoreFocus) this.lastFocusedElement?.focus?.();
    }

    handleSearchInput() {
      const query = this.searchInput?.value.trim() || '';
      window.clearTimeout(this.searchTimer);
      this.searchAbortController?.abort();

      if (query.length < 2) {
        this.resetPredictiveSearch();
        return;
      }

      this.searchTimer = window.setTimeout(() => this.fetchPredictiveSearch(query), 220);
    }

    resetPredictiveSearch() {
      this.search?.classList.remove('is-loading');
      if (this.searchResults) this.searchResults.hidden = true;
      if (this.searchResultsGrid) this.searchResultsGrid.replaceChildren();
      if (this.searchStatus) this.searchStatus.textContent = '';
      if (this.searchHint) this.searchHint.hidden = false;
    }

    formatSearchPrice(value) {
      if (value === null || value === undefined || value === '') return '';
      const text = String(value).trim();
      if (/[^0-9.,-]/.test(text)) return text;

      const number = Number(text.replace(/,/g, ''));
      if (!Number.isFinite(number)) return text;
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(number);
      } catch (_) {
        return text;
      }
    }

    buildPredictiveProduct(product) {
      const link = document.createElement('a');
      link.className = 'prereq-search-result-card';
      link.href = product.url || '#';
      link.setAttribute('role', 'listitem');

      const image = document.createElement('img');
      image.className = 'prereq-search-result-card__image';
      image.loading = 'lazy';
      image.alt = product.featured_image?.alt || product.title || 'PREREQ product';
      image.src = product.featured_image?.url || product.image || this.searchFallbackImage;
      image.width = 144;
      image.height = 144;
      link.appendChild(image);

      const body = document.createElement('div');
      body.className = 'prereq-search-result-card__body';

      const title = document.createElement('p');
      title.className = 'prereq-search-result-card__title';
      title.textContent = product.title || 'View product';
      body.appendChild(title);

      const meta = document.createElement('div');
      meta.className = 'prereq-search-result-card__meta';

      const price = document.createElement('span');
      price.textContent = this.formatSearchPrice(product.price);
      if (price.textContent) meta.appendChild(price);

      const stock = document.createElement('span');
      stock.className = `prereq-search-result-card__stock${product.available === false ? ' is-sold-out' : ''}`;
      stock.textContent = product.available === false ? 'Sold out' : 'In stock';
      meta.appendChild(stock);

      body.appendChild(meta);
      link.appendChild(body);
      return link;
    }

    async fetchPredictiveSearch(query) {
      if (!this.searchResults || !this.searchResultsGrid) return;

      this.searchAbortController = new AbortController();
      this.search?.classList.add('is-loading');
      this.searchResults.hidden = false;
      this.searchResultsGrid.replaceChildren();
      if (this.searchHint) this.searchHint.hidden = true;
      if (this.searchStatus) this.searchStatus.textContent = '';

      if (this.searchViewAll) {
        const viewAllUrl = new URL(this.fullSearchUrl, window.location.origin);
        viewAllUrl.searchParams.set('q', query);
        this.searchViewAll.href = viewAllUrl.toString();
      }

      try {
        const url = new URL(`${this.predictiveSearchUrl}.json`, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('resources[options][unavailable_products]', 'last');

        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.searchAbortController.signal
        });
        if (!response.ok) throw new Error('Search is temporarily unavailable.');

        const payload = await response.json();
        const products = payload?.resources?.results?.products || [];
        products.forEach((product) => this.searchResultsGrid.appendChild(this.buildPredictiveProduct(product)));

        if (this.searchStatus) {
          this.searchStatus.textContent = products.length
            ? `${products.length} suggested product${products.length === 1 ? '' : 's'} for “${query}”.`
            : `No product matches for “${query}”. Try a different term or view all search results.`;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[PREREQ] Predictive search failed', error);
        if (this.searchStatus) this.searchStatus.textContent = 'Search suggestions are unavailable right now. Press Enter to search the site.';
      } finally {
        this.search?.classList.remove('is-loading');
      }
    }

    setCartMessage(message = '', isError = false) {
      if (!this.cartMessage) return;
      this.cartMessage.textContent = message;
      this.cartMessage.hidden = !message;
      this.cartMessage.classList.toggle('is-error', isError);
    }

    setCartUpdating(updating) {
      this.cartUpdating = updating;
      this.cart?.classList.toggle('is-updating', updating);
      this.cart?.setAttribute('aria-busy', updating ? 'true' : 'false');
      this.cart?.querySelectorAll('[data-prereq-cart-quantity-change], [data-prereq-cart-remove]').forEach((button) => {
        button.disabled = updating;
      });
    }

    async handleCartClick(event) {
      const quantityButton = event.target.closest('[data-prereq-cart-quantity-change]');
      const removeButton = event.target.closest('[data-prereq-cart-remove]');
      if ((!quantityButton && !removeButton) || this.cartUpdating) return;

      const button = quantityButton || removeButton;
      const lineKey = button.dataset.cartLineKey;
      if (!lineKey) return;

      let quantity = 0;
      if (quantityButton) {
        const current = Number(quantityButton.dataset.cartCurrentQuantity || 0);
        const delta = Number(quantityButton.dataset.prereqCartQuantityChange || 0);
        quantity = Math.max(0, current + delta);
      }

      await this.changeCartLine(lineKey, quantity);
    }

    async changeCartLine(lineKey, quantity) {
      this.setCartMessage('');
      this.setCartUpdating(true);

      try {
        const action = new URL(this.cartChangeUrl, window.location.origin);
        action.pathname = action.pathname.replace(/\/$/, '') + '.js';
        const response = await fetch(action.toString(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: lineKey, quantity })
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.description || payload.message || 'Unable to update your cart.');
        }

        await this.refreshHeaderAndReopenCart();
      } catch (error) {
        console.error('[PREREQ] Cart update failed', error);
        this.setCartMessage(error.message || 'Unable to update your cart. Please try again.', true);
        this.setCartUpdating(false);
      }
    }

    async refreshHeaderAndReopenCart() {
      const headerSection = this.root.closest('.shopify-section');
      const sectionDomId = headerSection?.id || '';
      const sectionId = sectionDomId.replace(/^shopify-section-/, '');
      if (!headerSection || !sectionId) {
        window.location.reload();
        return;
      }

      const sectionUrl = new URL(window.location.href);
      sectionUrl.search = '';
      sectionUrl.hash = '';
      sectionUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(sectionUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Your cart changed, but the drawer could not be refreshed.');

      const html = await response.text();
      if (!html.trim()) throw new Error('Your cart changed, but the drawer could not be refreshed.');

      // The section is about to be replaced. Retire this controller so its
      // document/window listeners become inert after the fresh header mounts.
      this.destroy();

      headerSection.innerHTML = html;
      headerSection.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));

      requestAnimationFrame(() => {
        headerSection.querySelector('[data-prereq-cart-open]')?.click();
      });
    }

    handleDesktopDropdownToggle(changedDropdown) {
      if (!changedDropdown.open) return;
      this.closeDesktopDropdowns(changedDropdown);
    }

    handleMobileDetailsToggle(changedDetails) {
      if (!changedDetails.open) return;

      const isNested = changedDetails.classList.contains('prereq-mobile-menu__details--nested');
      const scope = isNested
        ? changedDetails.parentElement?.parentElement
        : changedDetails.parentElement?.parentElement;

      if (!scope) return;
      scope.querySelectorAll(':scope > li > [data-prereq-mobile-details]').forEach((details) => {
        if (details !== changedDetails && details.open) details.removeAttribute('open');
      });
    }

    handleBreakpointChange(event) {
      if (this.destroyed) return;
      if (!event.matches) {
        this.closeDesktopDropdowns();
        return;
      }

      if (this.activeOverlay === 'menu') this.closeOverlay('menu', false);
    }

    handleKeydown(event) {
      if (this.destroyed) return;
      if (event.key === 'Escape') {
        if (this.activeOverlay) {
          this.closeOverlay(this.activeOverlay);
        }
        this.closeDesktopDropdowns();
        return;
      }

      if (event.key !== 'Tab' || !this.activeOverlay) return;
      const overlay = this.getOverlay(this.activeOverlay);
      if (!overlay) return;

      const focusable = Array.from(
        overlay.querySelectorAll('a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleDocumentClick(event) {
      if (this.destroyed) return;
      this.desktopDropdowns.forEach((dropdown) => {
        if (dropdown.open && !dropdown.contains(event.target)) {
          dropdown.removeAttribute('open');
        }
      });
    }
  }

  const init = () => {
    document.querySelectorAll('[data-prereq-header]').forEach((root) => {
      if (root.dataset.prereqHeaderReady === 'true') return;
      root.dataset.prereqHeaderReady = 'true';
      root.prereqHeaderController = new PrereqHeader(root);
    });
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
